import React, { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";

// Lazily configure the pdfjs worker (CDN, matches installed version) once per app load.
let pdfjsLibPromise = null;
async function getPdfjs() {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import("pdfjs-dist").then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs";
      return pdfjsLib;
    });
  }
  return pdfjsLibPromise;
}

// Renders a small thumbnail of a PDF's first page. Falls back to a plain
// file icon if rendering fails (e.g. offline, corrupt file).
export default function PdfThumbnail({ url, width = 64, height = 84 }) {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await getPdfjs();
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min(width / viewport.width, height / viewport.height);
        const scaledViewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      } catch (err) {
        console.warn("PDF thumbnail render failed:", err?.message);
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, width, height]);

  if (failed) {
    return (
      <div
        style={{ width, height }}
        className="shrink-0 border border-white/15 bg-white/5 flex items-center justify-center"
      >
        <FileText size={20} strokeWidth={1.25} className="text-white/40" />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height }}
      className="shrink-0 border border-white/15 bg-white object-contain"
      data-testid="pdf-thumbnail-canvas"
    />
  );
}
